import React, { useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useDashboardSafe } from '../../contexts/DashboardContext';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  AccountCircle,
  Logout,
  Agriculture,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { DESIGN_TOKENS } from '../../design-system/tokens/colors';

export const AppHeader: React.FC = memo(() => {
  const { t } = useTranslation('common');
  const { authState, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  const dashboardContext = useDashboardSafe();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    window.location.href = '/';
  };

  const handleProfileClick = () => {
    handleMenuClose();
    
    // Always navigate to dashboard and show profile content
    if (location.pathname === '/dashboard' && dashboardContext) {
      // If already on dashboard, just update content
      dashboardContext.updateContent('profile');
    } else {
      // Navigate to dashboard with profile view
      navigate('/dashboard', { state: { contentView: 'profile' } });
    }
  };

  const handleFarmManagementClick = () => {
    handleMenuClose();
    
    // Navigate to dashboard and show user management content
    if (location.pathname === '/dashboard' && dashboardContext) {
      // If already on dashboard, just update content
      dashboardContext.updateContent('userManagement');
    } else {
      // Navigate to dashboard with user management view
      navigate('/dashboard', { state: { contentView: 'userManagement' } });
    }
  };

  // Removed plans navigation; plans moved to Profile

  const getUserInitials = () => {
    if (authState.user?.full_name) {
      return authState.user.full_name
        .split(' ')
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return authState.user?.username?.charAt(0).toUpperCase() || 'U';
  };

  return (
    <AppBar 
      position="static" // Changed from "fixed" to "static"
      elevation={1} 
      sx={{ 
        backgroundColor: 'white',
        borderBottom: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
        // Removed zIndex since it's no longer needed with static positioning
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', py: 1, minHeight: '64px' }}>
        {/* Logo Section */}
        <Box 
          component={RouterLink} 
          to="/"
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            textDecoration: 'none',
            cursor: 'pointer'
          }}
        >
          <img 
            src="/logo.svg" 
            alt="MUU Logo" 
            style={{ height: 32 }}
          />
        </Box>

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Removido: contador de tempo de sessão */}
          <LanguageSwitcher variant="compact" />
          
          {/* Removido: ícone de notificações */}

          {/* User Menu */}
          <IconButton
            size="large"
            onClick={handleMenuOpen}
            sx={{ ml: 1 }}
          >
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32, 
                bgcolor: 'primary.main',
                fontSize: '0.875rem'
              }}
            >
              {getUserInitials()}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            PaperProps={{
              elevation: 3,
              sx: {
                overflow: 'visible',
                filter: `drop-shadow(0px 2px 8px rgba(0, 0, 0, 0.15))`,
                mt: 1.5,
                minWidth: 200,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {/* User Info */}
            <MenuItem disabled>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                {getUserInitials()}
              </Avatar>
              <Box>
                <Typography variant="subtitle2">
                  {authState.user?.full_name || authState.user?.username}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {authState.user?.email}
                </Typography>
              </Box>
            </MenuItem>
            
            <Divider />
            
            {/* Menu Items */}
          <MenuItem onClick={handleProfileClick}>
            <ListItemIcon>
              <AccountCircle fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('navigation.profile')}</ListItemText>
          </MenuItem>
            
          <MenuItem onClick={handleFarmManagementClick}>
            <ListItemIcon>
              <Agriculture fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('navigation.organization')}</ListItemText>
          </MenuItem>
            
            {/* Removed: Plans, Settings and Help menu items */}
            
            <Divider />
            
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('navigation.logout')}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
});